/* eslint-disable max-lines */
/* eslint-disable complexity */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { roomIdSelector } from '@redux/room/selectors';
import useSelector from '@redux/useSelector';
import { setInstrumentalLoaded, setVocalGuideLoaded } from '@redux/videoPlayerResources';
import {
  backgroundRequestSelector,
  instrumentalRequestSelector,
  lyricsRequestSelector,
  swipeColorsRequestSelector,
  videoMetadataRequestSelector,
} from '@redux/videoPlayerResources/selectors';
import { RequestState } from '@redux/videoPlayerResources/types';
import { useDispatch } from 'react-redux';
import { useBoolean, useMedia } from 'react-use';
import { Song } from 'src/@types/api-types';
import useResizeObserver from 'use-resize-observer';

import {
  waitingListDownloadInstrumental,
  waitingListDownloadWithVoices,
} from 'components/shared/SharedPlayerArea/components/VideoPlayer.utils';
import VideoPlayer from 'components/shared/VideoPlayer';
import { isMobile } from 'services/helpers/browser';
import { isVocalGuideActivated } from 'services/helpers/featureToggle';
import { SECONDS_TO_MS_RATIO } from 'services/helpers/formatTime';
import {
  browserSupportsRequestFullscreenOnDiv,
  toggleFullscreenWithRequestFullscreen,
} from 'services/helpers/fullscreen';
import { useMediaElementListener } from 'services/hooks/useMediaElementListener';
import { SCOPE } from 'shared-types/constants';
import { PlayerSongDetails } from 'shared-types/player-event';
import { Mp3KbpVideoMetadata, VideoMetadata } from 'shared-types/video-metadata';
import { breakpoints } from 'stylesheetB2c';

import { MANUAL_TIME_UPDATE_EVENT_TYPE } from './Mp3KbpPlayer.constants';
import {
  Mp3KbpPlayerContainer,
  Mp3KbpPlayerVideoArea,
  SwitchToFullscreenContainer,
  VideoPlayerContainer,
} from './Mp3KbpPlayer.style';
import { getPreviewTime, allRessourcesLoaded, getVideoSize } from './Mp3KbpPlayer.utils';
import Background from './components/Background';
import Mp3KbpPlayerControls from './components/Mp3KbpPlayerControls';
import PlayerContent from './components/PlayerContent';

type Mp3KbpPlayerProps = {
  song: { id: string };
  activeVideoElement: HTMLVideoElement | null;
  setInstrumentalVideoElement: Dispatch<SetStateAction<HTMLVideoElement | null>>;
  setVocalGuideVideoElement?: Dispatch<SetStateAction<HTMLVideoElement | null>>;
  handlePlay: () => void;
  songDetails?: Song;
  scope: SCOPE;
  currentSongDetails?: PlayerSongDetails | null;
  nextSongDetails?: PlayerSongDetails | null;
  controls?: boolean;
  waitingListMetadata: VideoMetadata[];
  isStorageActivated: boolean;
};

const Mp3KbpPlayer: React.FC<Mp3KbpPlayerProps> = ({
  song,
  activeVideoElement,
  songDetails,
  setInstrumentalVideoElement,
  setVocalGuideVideoElement,
  handlePlay,
  controls = true,
  scope,
  currentSongDetails,
  nextSongDetails,
  waitingListMetadata,
  isStorageActivated,
}) => {
  const switchToFullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, toggleIsFullscreen] = useBoolean(false);
  const [isFullscreenWithCustomCSS, toggleFullscreenWithCustomCss] = useBoolean(false);
  const { height: videoAreaHeight, width: videoAreaWidth } = useResizeObserver<HTMLDivElement>({
    ref: switchToFullscreenContainerRef,
  });
  const [lastManualUpdateTimeInMs, setLastManualUpdateTimeInMs] = useState(0);
  const dispatch = useDispatch();

  const videoMetadataRequest = useSelector(videoMetadataRequestSelector);
  const backgroundRequest = useSelector(backgroundRequestSelector);
  const swipeColorsRequest = useSelector(swipeColorsRequestSelector);
  const lyricsRequest = useSelector(lyricsRequestSelector);
  const instrumentalRequest = useSelector(instrumentalRequestSelector);
  const roomId = useSelector(roomIdSelector);

  const colorSetting = songDetails?.colorSetting;

  const isDesktop = useMedia(`(min-width: ${breakpoints.wideDevices})`) && !isMobile();

  const { videoHeight, videoWidth } = useMemo(
    () => getVideoSize(videoAreaWidth ?? 0, videoAreaHeight ?? 0, isDesktop),
    [isDesktop, videoAreaHeight, videoAreaWidth],
  );

  const videoMetadata = videoMetadataRequest.value as Mp3KbpVideoMetadata | undefined;

  useMediaElementListener(activeVideoElement, MANUAL_TIME_UPDATE_EVENT_TYPE, () => {
    if (!activeVideoElement || !videoMetadata) {
      return;
    }
    setLastManualUpdateTimeInMs(
      videoMetadata.isPremium
        ? activeVideoElement.currentTime * SECONDS_TO_MS_RATIO
        : getPreviewTime(activeVideoElement.currentTime) * SECONDS_TO_MS_RATIO,
    );
  });

  const toggleFullscreen = useCallback(() => {
    const switchToFullscreenElement = switchToFullscreenContainerRef?.current;
    if (!switchToFullscreenElement) {
      return;
    }

    if (browserSupportsRequestFullscreenOnDiv(switchToFullscreenElement)) {
      // If requestFullscreen is supported, use this to switch to fullscreen
      toggleFullscreenWithRequestFullscreen(switchToFullscreenElement);
    } else {
      // If requestFullscreen is not supported, use custom css fullscreen
      toggleFullscreenWithCustomCss();
    }

    // Toggle isFullscreen state
    toggleIsFullscreen();
  }, [toggleFullscreenWithCustomCss, toggleIsFullscreen]);

  const resetPlayerState = useCallback(() => {
    setLastManualUpdateTimeInMs(0);
  }, []);

  const isPlayerReady =
    instrumentalRequest.state === RequestState.success &&
    allRessourcesLoaded(lyricsRequest.state, swipeColorsRequest.state, videoMetadataRequest.state);

  useEffect(() => {
    if (isPlayerReady && backgroundRequest.state === RequestState.success) {
      handlePlay();
    }
  }, [
    handlePlay,
    isPlayerReady,
    backgroundRequest.state,
    lyricsRequest,
    swipeColorsRequest,
    song.id,
    colorSetting,
    isStorageActivated,
    videoMetadataRequest.state,
  ]);

  useEffect(() => {
    resetPlayerState();
  }, [resetPlayerState, song]);

  return (
    <>
      {/*
        This video player will be hidden to only keep the sound
        The visual frames are displayed in the Mp3KbpPlayerVideoArea,
        generated from a fixed background image and overlayed text
        with timing parameterized in dedicated json file
      */}
      <VideoPlayerContainer>
        <>
          <VideoPlayer
            setVideoElement={setInstrumentalVideoElement}
            licenseToken={videoMetadata?.licenseToken}
            videoUrl={videoMetadata?.videoUrl}
            onVideoLoadEnd={() => dispatch(setInstrumentalLoaded())}
            playerType='instrumentalPlayer'
            waitingListDownloadMetadata={waitingListDownloadInstrumental(waitingListMetadata)}
            isStorageActivated={isStorageActivated}
            roomId={roomId}
          />
          {isVocalGuideActivated() &&
            videoMetadata?.isVocalGuideAvailable &&
            setVocalGuideVideoElement && (
              <VideoPlayer
                setVideoElement={setVocalGuideVideoElement}
                licenseToken={videoMetadata?.instrumentalWithVoicesLicenseToken}
                videoUrl={videoMetadata?.instrumentalWithVoicesFileUrl}
                onVideoLoadEnd={() => dispatch(setVocalGuideLoaded())}
                playerType='vocalGuidePlayer'
                waitingListDownloadMetadata={waitingListDownloadWithVoices(waitingListMetadata)}
                isStorageActivated={isStorageActivated}
                roomId={roomId}
              />
            )}
        </>
      </VideoPlayerContainer>
      <Mp3KbpPlayerContainer>
        <SwitchToFullscreenContainer
          ref={switchToFullscreenContainerRef}
          isFullscreenWithCustomCSS={isFullscreenWithCustomCSS}
        >
          <Mp3KbpPlayerVideoArea height={videoHeight} width={videoWidth}>
            {colorSetting && (
              <Background
                colorSetting={colorSetting}
                scope={scope}
                currentSongDetails={currentSongDetails}
                nextSongDetails={nextSongDetails}
                videoAreaHeight={videoHeight}
                visible={isPlayerReady}
              />
            )}
            {activeVideoElement && isPlayerReady && (
              <PlayerContent
                videoElement={activeVideoElement}
                isPremium={videoMetadata?.isPremium ?? false}
                songDetails={songDetails}
                currentSongDetails={currentSongDetails}
                videoAreaHeight={videoHeight}
                lyrics={lyricsRequest.value}
                swipeColors={swipeColorsRequest.value}
                lastManualUpdateTimeInMs={lastManualUpdateTimeInMs}
                scope={scope}
              />
            )}
            {controls && activeVideoElement && (
              <Mp3KbpPlayerControls
                handlePlay={handlePlay}
                isFullscreen={isFullscreen}
                toggleFullscreen={toggleFullscreen}
                videoElement={activeVideoElement}
              />
            )}
          </Mp3KbpPlayerVideoArea>
        </SwitchToFullscreenContainer>
      </Mp3KbpPlayerContainer>
    </>
  );
};

export default Mp3KbpPlayer;
